import { Divider, MenuItem, TextField } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  TsrctInitiationMode,
  TsrctNegotiationMode,
  TsrctNegotiationObjective,
  TsrctQuoteMode,
  TsrctSupplierInteractionMode,
} from '@/generated/gql/graphql';
import { useConfig } from '~/components/ConfigProvider';
import { useFormFieldErrorTranslation } from '~/hooks';

import { NegotiationTermsFormQuoteModeItem } from './components/NegotiationTermsFormQuoteModeItem';
import { commonInputProps } from './NegotiationOptionsLegacyForm.common';
import { NegotiationOptionsLegacyFormData } from './NegotiationOptionsLegacyForm.schema';

export const NegotiationOptionsLegacyForm = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'NegotiationTerms' });
  const { errorToHelperText } = useFormFieldErrorTranslation();
  const { features } = useConfig();

  const { control, watch, setValue } = useFormContext<NegotiationOptionsLegacyFormData>();

  const negotiationMode = watch('options.negotiationMode');

  // TODO remove as soon as final-offer is supported backend-wise for line-item negotiation
  useEffect(() => {
    if (negotiationMode === TsrctNegotiationMode.LINE_ITEM) {
      setValue('options.supplierInteractionMode', TsrctSupplierInteractionMode.COMMENT_ENABLED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negotiationMode]);

  return (
    <>
      <Controller
        control={control}
        name="options.negotiationMode"
        render={({ field, formState: { errors } }) => (
          <TextField
            {...field}
            select
            value={field.value || null}
            onChange={(e) => {
              field.onChange(e.target.value);
            }}
            label={t('options.negotiationMode.label')}
            error={!!errors.options?.negotiationMode}
            helperText={errorToHelperText(errors.options?.negotiationMode)}
            {...commonInputProps}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          >
            <MenuItem value={TsrctNegotiationMode.SUPPLIER_SELECTION}>
              {t('options.negotiationMode.SUPPLIER_SELECTION')}
            </MenuItem>
            <MenuItem value={TsrctNegotiationMode.QUOTE}>{t('options.negotiationMode.QUOTE')}</MenuItem>
            <MenuItem value={TsrctNegotiationMode.LINE_ITEM}>{t('options.negotiationMode.LINE_ITEM')}</MenuItem>
          </TextField>
        )}
      />
      <Divider />
      <Controller
        control={control}
        name="options.quoteMode"
        render={({ field, formState: { errors } }) => (
          <TextField
            {...field}
            select
            value={field.value || null}
            onChange={(e) => {
              field.onChange(e.target.value);
            }}
            label={t('options.quoteMode.label')}
            error={!!errors.options?.quoteMode}
            helperText={errorToHelperText(errors.options?.quoteMode)}
            {...commonInputProps}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          >
            <NegotiationTermsFormQuoteModeItem key={TsrctQuoteMode.NOOP} value={TsrctQuoteMode.NOOP}>
              {t('options.quoteMode.NOOP')}
            </NegotiationTermsFormQuoteModeItem>
            <NegotiationTermsFormQuoteModeItem key={TsrctQuoteMode.BID_SHEET} value={TsrctQuoteMode.BID_SHEET}>
              {t('options.quoteMode.BID_SHEET')}
            </NegotiationTermsFormQuoteModeItem>
            <NegotiationTermsFormQuoteModeItem
              disabled={false}
              key={TsrctQuoteMode.BID_SHEET_PROPOSAL}
              value={TsrctQuoteMode.BID_SHEET_PROPOSAL}
            >
              {t('options.quoteMode.BID_SHEET_PROPOSAL')}
            </NegotiationTermsFormQuoteModeItem>
          </TextField>
        )}
      />
      <Divider />
      <Controller
        control={control}
        name="options.initiatedBy"
        render={({ field, formState: { errors } }) => (
          <TextField
            {...field}
            select
            value={field.value || null}
            onChange={(e) => {
              field.onChange(e.target.value);
            }}
            label={t('options.initiatedBy.label')}
            error={!!errors.options?.initiatedBy}
            helperText={errorToHelperText(errors.options?.initiatedBy)}
            {...commonInputProps}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          >
            <MenuItem value={TsrctInitiationMode.PROCUREAI}>{t('options.initiatedBy.PROCUREAI')}</MenuItem>
            <MenuItem value={TsrctInitiationMode.SUPPLIER}>{t('options.initiatedBy.SUPPLIER')}</MenuItem>
          </TextField>
        )}
      />
      <Divider />
      <Controller
        control={control}
        name="options.supplierInteractionMode"
        render={({ field, formState: { errors } }) => (
          <TextField
            {...field}
            select
            value={field.value || null}
            onChange={(e) => {
              field.onChange(e.target.value);
            }}
            disabled={negotiationMode === TsrctNegotiationMode.LINE_ITEM}
            label={t('options.supplierInteractionMode.label')}
            error={!!errors.options?.supplierInteractionMode}
            helperText={errorToHelperText(errors.options?.supplierInteractionMode)}
            {...commonInputProps}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          >
            <MenuItem value={TsrctSupplierInteractionMode.COMMENT_ENABLED}>
              {t('options.supplierInteractionMode.COMMENT_ENABLED')}
            </MenuItem>
            <MenuItem value={TsrctSupplierInteractionMode.FINAL_OFFER_ENABLED}>
              {t('options.supplierInteractionMode.FINAL_OFFER_ENABLED')}
            </MenuItem>
          </TextField>
        )}
      />
      <Divider />
      <Controller
        control={control}
        name="options.negotiationObjective"
        render={({ field, formState: { errors } }) => (
          <TextField
            {...field}
            select
            value={field.value || null}
            onChange={(e) => {
              field.onChange(e.target.value);
            }}
            label={t('options.negotiationObjective.label')}
            disabled={!features.enableNegotiationObjective}
            error={!!errors.options?.negotiationObjective}
            helperText={errorToHelperText(errors.options?.negotiationObjective)}
            {...commonInputProps}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          >
            <MenuItem value={TsrctNegotiationObjective.PRIORITISE_COMMERCIAL_RESULT}>
              {t('options.negotiationObjective.PRIORITISE_COMMERCIAL_RESULT')}
            </MenuItem>
            <MenuItem value={TsrctNegotiationObjective.PRIORITISE_CLOSING}>
              {t('options.negotiationObjective.PRIORITISE_CLOSING')}
            </MenuItem>
          </TextField>
        )}
      />
    </>
  );
};
